import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, Home, Play } from "lucide-react";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import LeadForm from "@/components/site/LeadForm";
import Seo from "@/components/site/Seo";
import VideoSplash, { OPEN_SPLASH_EVENT } from "@/components/site/VideoSplash";
import usePageView from "@/lib/usePageView";
import useSiteContent from "@/lib/useSiteContent";
import { useFeatureFlags } from "@/lib/featureFlags";
import GoogleReviewsSection from "@/components/site/GoogleReviewsSection";
import { SERVICES, WHY_US, INDUSTRIES, PROCESS, PHONE_DISPLAY, PHONE_TEL } from "@/lib/siteContent";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProductRecommender from "@/components/ProductRecommender";

const HERO_IMG = "https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=1400&q=55";
const TRUST_IMG = "https://images.pexels.com/photos/10376233/pexels-photo-10376233.jpeg?auto=compress&cs=tinysrgb&fm=webp&w=1000";
const IMG_TRANSPORT = "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=800&q=60";
const IMG_CONSTRUCTION = "https://images.pexels.com/photos/8960946/pexels-photo-8960946.jpeg?auto=compress&cs=tinysrgb&fm=webp&w=800";

const HOME_SERVICE_COPY = {
  "working-capital-loans": "Maintain your day-to-day operations without interruption. Our working capital loans provide the immediate liquidity needed to cover payroll, rent, inventory purchases, and unforeseen expenses, keeping your business running smoothly during seasonal shifts or rapid expansion.",
  "merchant-cash-advance": "Leverage your future credit card sales for immediate cash. An MCA is a fast, unsecured funding option that fluctuates with your daily sales volume, making repayment highly flexible and stress-free during slower business months.",
  "equipment-financing": "Upgrade your technology, heavy machinery, or company vehicles without depleting your primary cash reserves. We provide competitive equipment financing and leasing options that allow you to acquire the essential tools necessary for growth while spreading the cost over time.",
  "sba-loans": "Benefit from government-backed Small Business Administration (SBA) loans. These highly sought-after loans offer lower down payments, longer repayment terms, and highly competitive interest rates for businesses looking to make substantial, long-term investments in their future.",
  "business-line-of-credit": "Secure a revolving credit limit that you can draw from whenever the need arises. You only pay interest on the funds you actually draw and use, making a business line of credit an excellent safety net for unexpected opportunities or sudden emergencies.",
  "commercial-real-estate-loans": "Whether you are purchasing a new office space, refinancing an existing commercial property, or funding a major facility renovation, our commercial real estate loans provide the substantial capital required with terms customized to your property\u2019s cash flow.",
};

const HOME_FAQS = [
  {
    q: "Can I get same-day business funding?",
    a: "Yes — many approved businesses receive same-day business funding. Approvals arrive in as soon as 2 hours, and once approved, funds can be wired in many cases the same business day. Apply online in about 5 minutes or call (888) 550-9339.",
  },
  {
    q: "How fast can I get approved for a business loan?",
    a: "Most applications are approved in as soon as 2 hours. Our streamlined underwriting looks at your business's real performance — not just paperwork — so decisions come back fast, often the same morning you apply.",
  },
  {
    q: "Can I get a business loan with bad credit?",
    a: "Yes. We consider credit scores from 500+ and weigh your business's revenue and overall performance, not just your personal credit. Businesses declined by banks are frequently approved through our programs.",
  },
  {
    q: "What do I need to apply?",
    a: "Just a 5-minute online application to start. For most programs we'll ask for your last 3 months of business bank statements; larger requests may need a recent tax return. Many options require no collateral.",
  },
];

const HOME_FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HOME_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function MarketingHomePage() {
  usePageView("/");
  const sc = useSiteContent();
  const home = sc.home || {};
  const helocEnabled = !!useFeatureFlags()?.heloc_enabled;
  // Preload the hero background (CSS backgrounds are discovered late) so
  // first paint doesn't wait on it.
  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = HERO_IMG;
    document.head.appendChild(link);
    return () => link.remove();
  }, []);
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }} data-testid="marketing-home-page">
      <Seo
        title="Encore Commercial Capital Corporation | Business Financing"
        description="Encore Commercial Capital Corporation offers fast, flexible business financing, SBA loans, and working capital. Approvals in 2 hours! Call (888) 550-9339."
        path="/"
        jsonLd={HOME_FAQ_JSONLD}
      />
      <VideoSplash />
      <SiteHeader />

      {/* Hero */}
      <section className="relative" data-testid="home-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0" style={{ background: "rgba(14,21,37,0.78)" }} />
        <div className="relative px-4 sm:px-6 md:px-10 lg:px-12 py-16 sm:py-24 max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 mk-up">
            <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "#9DB8DB" }} data-testid="home-eyebrow">
              {home.eyebrow || "The Alternative Lending Source"}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter mt-4" style={{ color: "#FFFFFF" }} data-testid="home-headline">
              {home.headline || "Encore Commercial Capital — Fast, Flexible Commercial Real Estate & Business Financing"}
            </h1>
            <p className="text-base sm:text-lg mt-6 max-w-xl" style={{ color: "rgba(255,255,255,0.75)" }}>
              {home.subheadline || `We help small and growing businesses get the funding they need — fast, simple,
              and tailored to your goals. Working capital, equipment, SBA loans, lines of
              credit,${helocEnabled ? " HELOC for business purposes," : ""} and commercial real estate loans, guided every step of the way.`}
            </p>
            <ul className="mt-8 space-y-2.5">
              {(home.bullets?.length ? home.bullets : ["Approvals in as soon as 2 hours", "We can fund in many cases the same business day",
                "Credit scores from 500+ considered", "Apply online or by phone — minimal paperwork"]).map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-sm text-white/90">
                  <ShieldCheck size={16} strokeWidth={1.5} color="#8FCBAA" /> {t}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link to="/apply" className="btn-primary" data-testid="home-apply-btn">
                Apply in 5 minutes <ArrowRight size={14} />
              </Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event(OPEN_SPLASH_EVENT))}
                className="inline-flex items-center gap-2.5 text-sm font-medium text-white"
                data-testid="home-watch-story-btn"
              >
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 38, height: 38, borderRadius: 999,
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.35)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Play size={14} strokeWidth={2} fill="#FFFFFF" />
                </span>
                Watch our story
              </button>
              <a href={PHONE_TEL} className="inline-flex items-center gap-2 text-sm font-medium text-white" data-testid="home-phone-btn">
                <Phone size={15} strokeWidth={1.5} /> or call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 mk-up" style={{ animationDelay: "120ms" }}>
            <LeadForm />
          </div>
        </div>
      </section>

      {/* Trusted alternative lending source */}
      <section className="px-4 sm:px-6 md:px-10 lg:px-12 py-20 sm:py-24 max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="home-trusted-source">
        <div className="lg:col-span-5">
          <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "var(--primary)" }}>Who we are</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mt-3" style={{ color: "var(--ink)" }}>
            Your Trusted Alternative Lending Source
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-5">
          <p className="text-base leading-relaxed" style={{ color: "var(--muted-fg)" }}>
            At Encore Commercial Capital Corporation, we understand that traditional bank loans do not always align
            with the fast-paced needs of modern businesses. As a premier alternative lending source, we are dedicated
            to providing fast, flexible business financing tailored to your specific industry and goals. Whether you
            are looking to expand your operations, purchase new inventory, or bridge a temporary cash flow gap, our
            comprehensive suite of funding solutions ensures you have the capital you need, exactly when you need it.
          </p>
          <p className="text-base leading-relaxed" style={{ color: "var(--muted-fg)" }}>
            Time is money, which is why our streamlined application and underwriting process allows for funding
            approvals in as soon as 2 hours. We focus on your business&rsquo;s potential and performance, rather than just
            a credit score, making us the ideal financial partner for companies seeking reliable commercial capital
            in today&rsquo;s competitive market.
          </p>
        </div>
      </section>

      {/* Services bento */}
      <section className="px-4 sm:px-6 md:px-10 lg:px-12 pb-20 sm:pb-24 max-w-[1280px] mx-auto" data-testid="home-services">
        <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "var(--primary)" }}>Financing programs</p>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mt-3 max-w-2xl" style={{ color: "var(--ink)" }}>
          Comprehensive Commercial Capital Services
        </h2>
        <p className="text-base mt-4 max-w-2xl" style={{ color: "var(--muted-fg)" }}>
          We offer a diverse range of financial products designed to meet the unique challenges of your business.
          Explore our core financing options below to see how we can fuel your growth:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {SERVICES.map((s, i) => (
            <Link key={s.slug} to={`/services/${s.slug}`}
                  className={`surface p-7 group transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg mk-up ${i === 0 ? "lg:col-span-2" : ""}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`home-service-${s.slug}`}>
              <s.icon size={26} strokeWidth={1.5} color="var(--primary)" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4" style={{ color: "var(--ink)" }}>{s.name}</h3>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--muted-fg)" }}>
                {sc.services?.[s.slug]?.short || HOME_SERVICE_COPY[s.slug] || s.short}
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium mt-4 transition-colors group-hover:text-[var(--primary-dark)]" style={{ color: "var(--primary)" }}>
                Get started <ArrowRight size={13} />
              </span>
            </Link>
          ))}
          {helocEnabled && (
          <Link to="/heloc"
                className="surface p-7 group transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg mk-up"
                style={{ animationDelay: `${SERVICES.length * 60}ms`, borderLeft: "3px solid var(--primary)" }}
                data-testid="home-service-heloc">
            <div className="flex items-start justify-between">
              <Home size={26} strokeWidth={1.5} color="var(--primary)" />
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1"
                    style={{ background: "var(--primary)", color: "#FFFFFF" }}>
                New
              </span>
            </div>
            <h3 className="font-display text-xl tracking-tight font-medium mt-4" style={{ color: "var(--ink)" }}>
              HELOC — Home Equity Line of Credit
            </h3>
            <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--muted-fg)" }}>
              Tap the equity in property you already own for business purposes. A 5-minute inquiry
              with a soft credit pull — no impact to your score.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium mt-4 transition-colors group-hover:text-[var(--primary-dark)]" style={{ color: "var(--primary)" }}>
              Start your inquiry <ArrowRight size={13} />
            </span>
          </Link>
          )}
        </div>
      </section>

      {/* Find your loan — product recommender */}
      <section className="px-4 sm:px-6 md:px-10 lg:px-12 pb-20 sm:pb-24 max-w-[1280px] mx-auto" data-testid="home-product-finder">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "var(--primary)" }}>Not sure where to start?</p>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mt-3" style={{ color: "var(--ink)" }}>
              Find your loan in 3 quick questions
            </h2>
            <p className="text-base mt-5 leading-relaxed" style={{ color: "var(--muted-fg)" }}>
              Every deal is different. Tell us what you're financing and we'll point you at the
              product that fits — then apply with it in minutes.
            </p>
          </div>
          <div className="lg:col-span-8 surface p-6 sm:p-8" style={{ border: "1px solid var(--border)" }}>
            <ProductRecommender applyLink="/apply" source="homepage" />
          </div>
        </div>
      </section>

      {/* Why choose us — grid borders */}
      <section style={{ background: "var(--surface)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }} data-testid="home-why-us">
        <div className="px-4 sm:px-6 md:px-10 lg:px-12 py-20 sm:py-24 max-w-[1280px] mx-auto">
          <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "var(--primary)" }}>Why Encore</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mt-3 max-w-2xl" style={{ color: "var(--ink)" }}>
            Why Partner With Encore Commercial Capital?
          </h2>
          <p className="text-base mt-5 max-w-3xl leading-relaxed" style={{ color: "var(--muted-fg)" }}>
            Choosing Encore Commercial Capital Corporation means partnering with dedicated financial experts who
            prioritize your long-term success. We bypass the restrictive red tape and endless paperwork associated
            with traditional banking institutions to deliver a seamless, frictionless lending experience. Our
            dedicated funding advisors work closely one-on-one with you to understand your specific business model
            and capital needs, ensuring you receive the most advantageous lending terms possible. With
            industry-leading turnaround times—including approvals in as little as 2 hours—you can seize
            time-sensitive growth opportunities without delay.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mt-12" style={{ border: "1px solid var(--border)" }}>
            {WHY_US.map((w, i) => (
              <div key={w.title} className="p-8"
                   style={{ borderRight: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}
                   data-testid={`home-why-${i}`}>
                <p className="font-mono text-xs" style={{ color: "var(--primary)" }}>{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-display text-lg font-medium mt-3" style={{ color: "var(--ink)" }}>{w.title}</h3>
                <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--muted-fg)" }}>{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="px-4 sm:px-6 md:px-10 lg:px-12 py-20 sm:py-24 max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="home-process">
        <div className="lg:col-span-5">
          <img src={TRUST_IMG} alt="Business owner reviewing financing options"
               loading="lazy" decoding="async" width="1000" height="667"
               className="w-full object-cover" style={{ borderRadius: 4, maxHeight: "70vh", border: "1px solid var(--border)" }} />
        </div>
        <div className="lg:col-span-7">
          <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "var(--primary)" }}>How it works</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mt-3" style={{ color: "var(--ink)" }}>
            Get funded quickly and confidently
          </h2>
          <div className="mt-10 space-y-8">
            {PROCESS.map((p) => (
              <div key={p.step} className="flex gap-5">
                <span className="font-mono text-sm mt-1" style={{ color: "var(--primary)" }}>{p.step}</span>
                <div>
                  <h3 className="font-display text-lg font-medium" style={{ color: "var(--ink)" }}>{p.title}</h3>
                  <p className="text-sm mt-1" style={{ color: "var(--muted-fg)" }}>{p.body}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/apply" className="btn-primary mt-10 inline-flex" data-testid="home-process-apply-btn">
            Get pre-qualified now <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Google reviews */}
      <GoogleReviewsSection />

      {/* Industries */}
      <section style={{ background: "var(--ink)" }} data-testid="home-industries">
        <div className="px-4 sm:px-6 md:px-10 lg:px-12 py-20 sm:py-24 max-w-[1280px] mx-auto">
          <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "#9DB8DB" }}>Industries we serve</p>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mt-3 max-w-2xl" style={{ color: "#FFFFFF" }}>
            Specialized funding for the way your industry works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-12">
            <div className="md:col-span-4">
              <img src={IMG_TRANSPORT} alt="Transportation business loans" loading="lazy" decoding="async" className="w-full h-48 object-cover" style={{ borderRadius: 4 }} />
              <p className="text-sm mt-3 text-white/80">Transportation & logistics</p>
            </div>
            <div className="md:col-span-4">
              <img src={IMG_CONSTRUCTION} alt="Construction business loans" loading="lazy" decoding="async" className="w-full h-48 object-cover" style={{ borderRadius: 4 }} />
              <p className="text-sm mt-3 text-white/80">Construction & trades</p>
            </div>
            <div className="md:col-span-4 flex flex-wrap gap-2.5 content-start">
              {INDUSTRIES.map((ind) => (
                <span key={ind} className="px-4 py-2 text-sm"
                      style={{ border: "1px solid rgba(255,255,255,0.2)", borderRadius: 999, color: "rgba(255,255,255,0.85)" }}
                      data-testid={`home-industry-${ind.toLowerCase().replace(/\s/g, "-")}`}>
                  {ind}
                </span>
              ))}
              <p className="text-xs mt-2 w-full" style={{ color: "rgba(255,255,255,0.5)" }}>
                …and many more. If your business earns revenue, we likely have a program for it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 md:px-10 lg:px-12 py-20 max-w-[1280px] mx-auto" data-testid="home-faq">
        <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "var(--primary)" }}>Common questions</p>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mt-3" style={{ color: "var(--ink)" }}>
          Fast answers on fast funding
        </h2>
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="mt-6">
            {HOME_FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`home-faq-${i}`} data-testid={`home-faq-item-${i}`}>
                <AccordionTrigger className="text-left text-sm font-medium" style={{ color: "var(--ink)" }} data-testid={`home-faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed" style={{ color: "var(--body)" }}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="text-sm mt-5" style={{ color: "var(--muted-fg)" }}>
            More questions? Visit our <Link to="/faq" className="link" data-testid="home-faq-see-all">full FAQ</Link> or
            call <a href={PHONE_TEL} className="link">{PHONE_DISPLAY}</a>.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 sm:px-6 md:px-10 lg:px-12 py-20 max-w-[1280px] mx-auto text-center" data-testid="home-final-cta">
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium" style={{ color: "var(--ink)" }}>
          Apply for Alternative Business Financing Today
        </h2>
        <p className="text-base mt-4 max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--muted-fg)" }}>
          Do not let a lack of capital hold your business back from reaching its full potential. Experience the
          difference of working with a responsive, client-focused alternative lender. Contact Encore Commercial
          Capital Corporation today to speak directly with one of our experienced funding specialists and discover
          which tailored financial solution is exactly right for your enterprise.
        </p>
        <p className="text-base mt-4 max-w-2xl mx-auto font-semibold" style={{ color: "var(--ink)" }}>
          Call us now at <a href={PHONE_TEL} className="underline underline-offset-2" style={{ color: "var(--primary)" }} data-testid="home-cta-phone-link">{PHONE_DISPLAY}</a> to
          get started and receive your funding approval in as soon as 2 hours!
        </p>
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <Link to="/apply" className="btn-primary" data-testid="home-cta-apply-btn">Apply now <ArrowRight size={14} /></Link>
          <Link to="/get-funded" className="btn-ghost" data-testid="home-cta-get-funded-btn">Get funded in 60 seconds</Link>
          <Link to="/contact" className="btn-ghost" data-testid="home-cta-contact-btn">Talk to a specialist</Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
